import { useIsRtl } from '@/hooks/useIsRtl';
import { Optional } from '@/types';
import { Fade } from '@mui/material';
import styles from './styles.module.scss';
import { IconChevronDown2MUI } from '../../Icons/Chevron';
import { IconCheck2 } from '@/blocks/components/Icons';
import MenuItem from '@mui/material/MenuItem';
import { default as MUISelect, SelectProps as MUISelectProps } from '@mui/material/Select/Select';
import React, { useCallback, useEffect, useRef } from 'react';
import { throttle } from '@/utils/throttle';

export function Select({
  values,
  ...props
}: Optional<MUISelectProps, 'variant'> & {
  values: { label: string; value: any }[];
}) {
  const threshold = 100;
  const [open, setOpen] = React.useState(false);
  const initialScrollY = useRef(0);

  const handleClose = useCallback(
    (e: any) => {
      if (props.onClose) {
        props.onClose(e);
      }
      setOpen(false);
    },
    [props],
  );

  const handleOpen = (e: any) => {
    if (props.onOpen) {
      props.onOpen(e);
    }
    initialScrollY.current = window.scrollY;
    setOpen(true);
  };

  useEffect(() => {
    const handleScroll = throttle(() => {
      const deltaY = Math.abs(window.scrollY - initialScrollY.current);
      if (deltaY > threshold) {
        handleClose(null);
      }
    }, 300);

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleClose, threshold, open]);

  const { isRtl } = useIsRtl();
  return (
    <MUISelect
      IconComponent={IconChevronDown2MUI}
      className={styles.select}
      MenuProps={{
        className: styles.selectDropdown,
        disableScrollLock: true,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: isRtl ? 'left' : 'right',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: isRtl ? 'left' : 'right',
        },
        TransitionComponent: Fade,
        disableRestoreFocus: true,
        ...props.MenuProps,
      }}
      open={open}
      onClose={handleClose}
      onOpen={handleOpen}
      {...props}
    >
      {values.map((v) => {
        return (
          <MenuItem key={v.value} disableRipple value={v.value}>
            <div className={styles.selectDropdownItemIconBox}>
              {props.value === v.value ? <IconCheck2 /> : null}
            </div>
            {v.label}
          </MenuItem>
        );
      })}
    </MUISelect>
  );
}
